'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type EntityType = 'company' | 'establishment' | 'individual' | 'other';

export interface Entity {
  id: string;
  owner_id: string;
  name: string;
  entity_type: EntityType;
  notes: string;
  is_active: boolean;
  created_at: string;
}

interface EntityContextValue {
  entities: Entity[];
  currentEntity: Entity | null;
  loading: boolean;
  setCurrentEntity: (entity: Entity | null) => void;
  refreshEntities: () => Promise<void>;
  createEntity: (data: { name: string; entity_type: EntityType; notes?: string }) => Promise<Entity>;
  updateEntity: (id: string, data: Partial<Entity>) => Promise<void>;
  deleteEntity: (id: string) => Promise<void>;
  assignEntityToUser: (entityId: string, userId: string) => Promise<void>;
  revokeEntityFromUser: (entityId: string, userId: string) => Promise<void>;
  getUserEntityIds: (userId: string) => Promise<string[]>;
}

const EntityContext = createContext<EntityContextValue>({} as EntityContextValue);

const STORAGE_KEY = 'docalert_current_entity_id';

export const useEntity = () => {
  const ctx = useContext(EntityContext);
  if (!ctx) throw new Error('useEntity must be used within EntityProvider');
  return ctx;
};

/** Restore or pick first entity from list */
function pickEntity(list: Entity[]): Entity | null {
  if (list.length === 0) return null;
  try {
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (savedId) {
      const found = list.find(e => e.id === savedId);
      if (found) return found;
    }
  } catch { /* ignore */ }
  return list[0];
}

export const EntityProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [currentEntity, setCurrentEntityState] = useState<Entity | null>(null);
  const [loading, setLoading] = useState(true);

  const setCurrentEntity = useCallback((entity: Entity | null) => {
    setCurrentEntityState(entity);
    try {
      if (entity) {
        localStorage.setItem(STORAGE_KEY, entity.id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* ignore */ }
  }, []);

  /** Fetch entities via entity_assignments (primary path) */
  const fetchViaAssignments = useCallback(async (userId: string): Promise<Entity[] | null> => {
    const { data, error } = await supabase
      .from('entity_assignments')
      .select('entity_id, entities!inner(*)')
      .eq('user_id', userId)
      .eq('entities.is_active', true)
      .order('entities(created_at)', { ascending: true });

    if (error) {
      // Table may not exist yet in schema cache — return null to trigger fallback
      if (error.code === 'PGRST205' || error.message?.includes('entity_assignments')) {
        return null;
      }
      throw error;
    }

    return (data || [])
      .map((row: any) => row.entities as Entity)
      .filter(Boolean);
  }, [supabase]);

  /** Fallback: fetch owned entities directly */
  const fetchOwnedEntities = useCallback(async (userId: string): Promise<Entity[]> => {
    const { data, error } = await supabase
      .from('entities')
      .select('*')
      .eq('owner_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []) as Entity[];
  }, [supabase]);

  const refreshEntities = useCallback(async () => {
    if (!user) {
      setEntities([]);
      setCurrentEntityState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Try primary path via entity_assignments
      let list = await fetchViaAssignments(user.id);

      if (list === null) {
        // entity_assignments table not ready — fall back to owned entities
        list = await fetchOwnedEntities(user.id);
      }

      setEntities(list);
      const picked = pickEntity(list);
      setCurrentEntityState(picked);
      if (picked) {
        try { localStorage.setItem(STORAGE_KEY, picked.id); } catch { /* ignore */ }
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
      // Last resort fallback
      try {
        let list = await fetchOwnedEntities(user.id);
        setEntities(list);
        const picked = pickEntity(list);
        setCurrentEntityState(picked);
        if (picked) {
          try { localStorage.setItem(STORAGE_KEY, picked.id); } catch { /* ignore */ }
        }
      } catch {
        setEntities([]);
        setCurrentEntityState(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user, fetchViaAssignments, fetchOwnedEntities]);

  useEffect(() => {
    if (authLoading) return;
    refreshEntities();
  }, [authLoading, refreshEntities]);

  const createEntity = async (data: { name: string; entity_type: EntityType; notes?: string }): Promise<Entity> => {
    if (!user) throw new Error('Not authenticated');
    const { data: created, error } = await supabase
      .from('entities')
      .insert({
        owner_id: user.id,
        name: data.name,
        entity_type: data.entity_type,
        notes: data.notes || '',
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;
    const entity = created as Entity;

    // Auto-assign the owner to the new entity (best-effort)
    try {
      await supabase.from('entity_assignments').upsert({
        entity_id: entity.id,
        user_id: user.id,
        assigned_by: user.id,
      }, { onConflict: 'entity_id,user_id' });
    } catch {
      // entity_assignments may not exist yet — not fatal
    }

    setEntities(prev => [...prev, entity]);
    if (!currentEntity) {
      setCurrentEntity(entity);
    }
    return entity;
  };

  const updateEntity = async (id: string, data: Partial<Entity>) => {
    const { error } = await supabase
      .from('entities')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    setEntities(prev => prev.map(e => e.id === id ? { ...e, ...data } : e));
    if (currentEntity?.id === id) {
      setCurrentEntityState(prev => prev ? { ...prev, ...data } : null);
    }
  };

  const deleteEntity = async (id: string) => {
    const { error } = await supabase
      .from('entities')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
    const remaining = entities.filter(e => e.id !== id);
    setEntities(remaining);
    if (currentEntity?.id === id) {
      setCurrentEntity(remaining[0] || null);
    }
  };

  const assignEntityToUser = async (entityId: string, userId: string) => {
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase
      .from('entity_assignments')
      .upsert({
        entity_id: entityId,
        user_id: userId,
        assigned_by: user.id,
      }, { onConflict: 'entity_id,user_id' });
    if (error) throw error;
  };

  const revokeEntityFromUser = async (entityId: string, userId: string) => {
    const { error } = await supabase
      .from('entity_assignments')
      .delete()
      .eq('entity_id', entityId)
      .eq('user_id', userId);
    if (error) throw error;
  };

  const getUserEntityIds = async (userId: string): Promise<string[]> => {
    const { data, error } = await supabase
      .from('entity_assignments')
      .select('entity_id')
      .eq('user_id', userId);
    if (error) {
      // Table not ready — return empty array gracefully
      if (error.code === 'PGRST205' || error.message?.includes('entity_assignments')) {
        return [];
      }
      throw error;
    }
    return (data || []).map((row: any) => row.entity_id);
  };

  return (
    <EntityContext.Provider value={{
      entities,
      currentEntity,
      loading,
      setCurrentEntity,
      refreshEntities,
      createEntity,
      updateEntity,
      deleteEntity,
      assignEntityToUser,
      revokeEntityFromUser,
      getUserEntityIds,
    }}>
      {children}
    </EntityContext.Provider>
  );
};
